import React, { type ComponentProps, forwardRef, useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Popup, type PopupComponentProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ColorPickerDropdown } from './dropdown';
import { ColorPickerTrigger } from './trigger';

export type ColorPickerProps = Pick<
	ComponentProps<typeof ColorPickerTrigger>,
	'isFullWidth' | 'showTextIcon' | 'analyticsSource' | 'isDisabled'
> &
	Pick<
		ComponentProps<typeof ColorPickerDropdown>,
		| 'builtInColorMap'
		| 'isReadableColor'
		| 'onChange'
		| 'value'
		| 'pickerTitle'
		| 'showCustomPalette'
		| 'showResetButton'
		| 'validationSuccessMessage'
		| 'validationFailMessage'
		| 'showCloseButton'
		| 'enableBorderLessStyle'
	> & {
		isOpen?: boolean;
	};

const popupComponentStyles = xcss({
	background: token('elevation.surface.overlay'),
	borderRadius: 'radius.small',
	boxShadow: 'elevation.shadow.overlay',
	padding: 'space.150',
});

const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>(
	({ 'data-testid': testId, ...props }, ref) => (
		<Box testId={testId} {...props} ref={ref} xcss={popupComponentStyles} />
	),
);

export const ColorPicker = ({
	builtInColorMap,
	isReadableColor,
	onChange,
	value,
	isFullWidth,
	pickerTitle,
	showCustomPalette,
	showResetButton,
	showTextIcon,
	validationSuccessMessage,
	validationFailMessage,
	analyticsSource,
	isDisabled = false,
	showCloseButton = false,
	enableBorderLessStyle = false,
	isOpen = false,
}: ColorPickerProps): React.JSX.Element => {
	const [isPopupOpen, setIsPopupOpen] = useState<boolean>(isOpen);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onPopupClose = () => setIsPopupOpen(false);

	const onTriggerClick = useCallback(() => {
		if (analyticsSource && !isPopupOpen) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'colorPickerOpened',
					source: analyticsSource,
				},
			}).fire();
		}

		setIsPopupOpen((prev) => !prev);
	}, [analyticsSource, createAnalyticsEvent, isPopupOpen]);

	return (
		<Popup
			autoFocus={false}
			content={(contentProps) => (
				<ColorPickerDropdown
					{...contentProps}
					analyticsSource={analyticsSource}
					builtInColorMap={builtInColorMap}
					isReadableColor={isReadableColor}
					onChange={onChange}
					value={value}
					pickerTitle={pickerTitle}
					showCustomPalette={showCustomPalette}
					showResetButton={showResetButton}
					validationSuccessMessage={validationSuccessMessage}
					validationFailMessage={validationFailMessage}
					showCloseButton={showCloseButton}
				/>
			)}
			isOpen={isPopupOpen}
			onClose={onPopupClose}
			placement="bottom-end"
			fallbackPlacements={['left-start']}
			testId="color-picker-popup"
			popupComponent={PopupComponent}
			trigger={(triggerProps) => (
				<ColorPickerTrigger
					{...triggerProps}
					builtInColorMap={builtInColorMap}
					onClick={onTriggerClick}
					value={value}
					isDisabled={isDisabled}
					analyticsSource={analyticsSource}
					isFullWidth={isFullWidth}
					showTextIcon={showTextIcon}
					pickerTitle={pickerTitle}
					enableBorderLessStyle={enableBorderLessStyle}
				/>
			)}
		/>
	);
};
